/**
 * Org-level employee cost rates for timesheet expense estimates.
 * Mirrors public/config/rate_config.json shape used by the legacy app.
 */

const ORG_EMPLOYEE_COST_DEFAULT = 20

let cachedConfig: RateConfig | null = null

export interface RateOverride {
	year: number
	month?: number | null
	rate: number
}

export interface RateConfig {
	default_rate_per_hour?: number
	overrides?: RateOverride[]
}

export async function loadRateConfig(): Promise<RateConfig> {
	if (cachedConfig) return cachedConfig
	try {
		const res = await fetch('/config/rate_config.json')
		if (res.ok) {
			cachedConfig = (await res.json()) as RateConfig
			return cachedConfig
		}
	} catch {
		/* use default */
	}
	cachedConfig = {
		default_rate_per_hour: ORG_EMPLOYEE_COST_DEFAULT,
		overrides: [],
	}
	return cachedConfig
}

export function resolveRate(
	config: RateConfig | null,
	year: number,
	month: number | null = null,
): number {
	const overrides = config?.overrides || []
	let yearMonthMatch: number | null = null
	let yearMatch: number | null = null
	for (const o of overrides) {
		if (o.year !== year) continue
		if (o.month != null && month != null && o.month === month) {
			yearMonthMatch = o.rate
		} else if (o.month == null) {
			yearMatch = o.rate
		}
	}
	return (
		yearMonthMatch ??
		yearMatch ??
		config?.default_rate_per_hour ??
		ORG_EMPLOYEE_COST_DEFAULT
	)
}

export function getRateForPeriod(
	config: RateConfig | null,
	year: number | null,
	month: number | null,
): number {
	if (!config) return ORG_EMPLOYEE_COST_DEFAULT
	const y = year != null ? Number(year) : null
	const m = month != null ? Number(month) : null
	if (y != null) return resolveRate(config, y, m)
	return config.default_rate_per_hour ?? ORG_EMPLOYEE_COST_DEFAULT
}
